import React from "react";
import { motion } from "framer-motion";
import { Recycle, PlusCircle, CheckCircle2, MapPin, Sparkles, Users, ShieldCheck, PackageOpen, Leaf, ShoppingBag, Eye } from "lucide-react";
import BrandStripe from "./BrandStripe";
import SectionHeader from "./SectionHeader";
import { TID } from "@/lib/testIds";

const values = [
  { key: "design-first",    title: "Design First",    text: "Readers should be something people actually enjoy wearing.", bg: "var(--ge-red)",   icon: <Eye size={28} /> },
  { key: "sustainability",  title: "Sustainability",  text: "Frames, lenses and packaging made from recycled materials.", bg: "var(--ge-green)", icon: <Recycle size={28} /> },
  { key: "simplicity",      title: "Simplicity",      text: "Shopping for reading glasses should be intuitive, not a puzzle.", bg: "var(--ge-navy)",  icon: <PlusCircle size={28} /> },
  { key: "accessibility",   title: "Accessibility",   text: "Premium design at an accessible price point. For everyone.", bg: "var(--ge-beige)", fg: "var(--ge-navy)", icon: <Users size={28} /> },
];

const pillars = [
  { title: "Designed in New Zealand", icon: <MapPin size={22} />, bg: "var(--ge-yellow)", fg: "var(--ge-navy)" },
  { title: "Recycled Co-Polyester Frames", icon: <Recycle size={22} />, bg: "var(--ge-green)", fg: "#fff" },
  { title: "Recycled Lenses", icon: <PackageOpen size={22} />, bg: "var(--ge-red)", fg: "#fff" },
  { title: "BPA Free", icon: <ShieldCheck size={22} />, bg: "var(--ge-yellow)", fg: "var(--ge-navy)" },
  { title: "Lightweight Construction", icon: <Leaf size={22} />, bg: "var(--ge-navy)", fg: "#fff" },
  { title: "Fashion Forward Shapes", icon: <Sparkles size={22} />, bg: "var(--ge-navy)", fg: "#fff" },
  { title: "Unisex Styling", icon: <Users size={22} />, bg: "var(--ge-beige)", fg: "var(--ge-navy)" },
  { title: "Colour-Coded Strengths", icon: <PlusCircle size={22} />, bg: "var(--ge-red)", fg: "#fff" },
  { title: "Recycled Felt Pouch", icon: <ShoppingBag size={22} />, bg: "var(--ge-green)", fg: "#fff" },
  { title: "Fully Recyclable Packaging", icon: <CheckCircle2 size={22} />, bg: "var(--ge-yellow)", fg: "var(--ge-navy)" },
];

export const NZStory = () => {
  return (
    <section
      id="story"
      data-testid={TID.nz.root}
      className="relative bg-white text-[color:var(--ge-navy)] py-24 md:py-32 overflow-hidden border-t border-[color:var(--ge-navy)]/10"
    >
      <div className="ge-container">
        <SectionHeader
          kicker="Brand Values"
          title={<>From a small country<br />with a <span style={{ color: "var(--ge-green)" }}>big view</span>.</>}
          subtitle="Good Eyes is a New Zealand-designed range of reading glasses created specifically for pharmacy retail. Our mission: to create beautifully designed reading glasses that make vision care simpler, more stylish and more sustainable."
        />

        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {values.map((v, i) => (
            <motion.article
              key={v.key}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              className="rounded-2xl p-6 md:p-8 flex items-start gap-5 min-h-[160px]"
              style={{ background: v.bg, color: v.fg || "#ffffff" }}
              data-testid={`value-card-${v.key}`}
            >
              <div className="shrink-0 h-14 w-14 rounded-full border-2 flex items-center justify-center" style={{ borderColor: v.fg || "#ffffff", color: v.fg || "#ffffff" }}>
                {v.icon}
              </div>
              <div className="flex-1">
                <h3 className="tight-cap font-bold text-2xl md:text-3xl uppercase leading-tight" style={{ color: v.fg || "#ffffff" }}>
                  {v.title}
                </h3>
                <div className="mt-2 h-[3px] w-10 rounded-full opacity-80" style={{ background: v.fg || "#ffffff" }} />
                <p className="mt-3 text-sm md:text-base font-light leading-relaxed" style={{ color: v.fg ? "rgba(0,31,91,0.85)" : "rgba(255,255,255,0.92)" }}>
                  {v.text}
                </p>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[color:var(--ge-navy)]/70">
              Product Pillars
            </div>
            <h2 className="mt-3 tight-cap font-bold text-[color:var(--ge-navy)] leading-[0.95] text-[clamp(2rem,4.5vw,3.6rem)]">
              What every frame<br />
              <span style={{ color: "var(--ge-red)" }}>gets right</span>.
            </h2>
            <div className="mt-4 h-[4px] w-14 rounded-full" style={{ background: "var(--ge-yellow)" }} />
            <p className="mt-6 text-base md:text-lg text-[color:var(--ge-navy)]/80 font-light leading-relaxed max-w-md">
              Ten commitments, from recycled co-polyester to a colour-coded
              strength system, that add up to a genuinely better pair of readers.
            </p>
          </div>

          <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {pillars.map((p, i) => (
              <motion.div key={p.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: i * 0.04 }} className="rounded-xl p-4 md:p-5 flex flex-col gap-3 border border-[color:var(--ge-navy)]/10 bg-white" data-testid={`pillar-${i}`}>
                <span className="inline-flex items-center justify-center h-11 w-11 rounded-full" style={{ background: p.bg, color: p.fg }}>
                  {p.icon}
                </span>
                <div className="text-sm md:text-base font-semibold text-[color:var(--ge-navy)] leading-tight">
                  {p.title}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-20 md:mt-24 border-t border-[color:var(--ge-navy)]/15 pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="text-[13px] md:text-base text-[color:var(--ge-navy)]/80 max-w-2xl font-light">
            Good Eyes frames and packaging are made from{" "}
            <span className="font-semibold" style={{ color: "var(--ge-green)" }}>100% recycled materials</span>.
          </div>
          <a href="/shop" data-testid={TID.nz.cta} className="inline-flex items-center gap-2 rounded-full bg-[color:var(--ge-navy)] text-white px-6 py-3 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-[color:var(--ge-green)] transition-colors self-start">
            Shop the range
          </a>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0">
        <BrandStripe height={22} />
      </div>
    </section>
  );
};

export default NZStory;
